import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Team {
  id: string;
  name: string;
  memberCount: number;
  isActive: boolean;
}

interface TeamsListResponse {
  teams: Array<{
    id: string;
    name: string;
    member_count: number;
    is_active: boolean;
  }>;
  totalCount: number;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Fetches all active teams for the tenant (for dropdown usage).
 * Returns a flat list of { id, name, memberCount } sorted by name.
 */
export function useTeams() {
  return useQuery({
    queryKey: ['teams', 'active-list'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<TeamsListResponse>>('/teams', {
        params: {
          pageSize: 100,
          sortBy: 'name',
          sortDirection: 'asc',
          status: 'active',
        },
      });

      return response.data.data.teams.map((t) => ({
        id: t.id,
        name: t.name,
        memberCount: t.member_count,
        isActive: t.is_active,
      }));
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
